import type { FilterState } from '@/stores/productListFilters';
import { FilterValidation } from '@/utils/validation/filterValidation';

/**
 * Drop only the persisted (localStorage) filter fields that fail validation,
 * keeping the rest. Guards against stale data from a prior visit — e.g. a
 * `dateRange` whose start is now in the past, or a `duration` inconsistent with
 * the stored `dateRange`.
 *
 * Shared by both consumers of `ibe_product_list_filters`:
 * - `useFilterInitializer` (product-list page)
 * - `useOfferInitializer` (offer page hydration fallback)
 *
 * so a stale date can't slip into one page's search state while being sanitized
 * away on the other.
 */
export function sanitizeStoredFilters(state: FilterState): FilterState {
  const sanitized: FilterState = { ...state };

  // Never honour a persisted duration mode — see ProductListFiltersStorage.write.
  // Only blobs written by an older build can carry one.
  delete sanitized.durationMode;

  if (sanitized.dateRange !== undefined) {
    let drop = false;
    if (!Array.isArray(sanitized.dateRange) || sanitized.dateRange.length !== 2) {
      drop = true;
    } else {
      const [start, end] = sanitized.dateRange;
      if (
        !FilterValidation.validateDateRange(start, end) ||
        !FilterValidation.isStartDateBookable(start)
      ) {
        drop = true;
      }
    }
    if (drop) {
      console.warn(
        '[sanitizeStoredFilters] localStorage dateRange invalid, dropping dateRange + duration'
      );
      delete sanitized.dateRange;
      delete sanitized.duration;
    }
  }

  if (sanitized.duration !== undefined && sanitized.duration !== null) {
    if (FilterValidation.validateDuration(sanitized.duration) === null) {
      console.warn('[sanitizeStoredFilters] localStorage duration invalid, dropping');
      delete sanitized.duration;
    }
  }

  if (
    sanitized.dateRange &&
    Array.isArray(sanitized.dateRange) &&
    sanitized.dateRange.length === 2 &&
    sanitized.duration !== undefined &&
    sanitized.duration !== null &&
    !FilterValidation.validatePeriodConsistency(
      sanitized.dateRange[0],
      sanitized.dateRange[1],
      sanitized.duration
    )
  ) {
    console.warn(
      '[sanitizeStoredFilters] localStorage duration exceeds dateRange, dropping duration'
    );
    delete sanitized.duration;
  }

  return sanitized;
}
